import React from 'react';
import { useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { CircleCheck, CircleX } from 'lucide-react';
import { configurationLogApi } from '@/api/production/logs';
import type { IConfigurationLog } from '@/types/production/configuration-log';
import { DEVICE_MODEL_LABELS } from '@/constants/device';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

function getStatusProps(value: boolean) {
  const statusClass = value ? 'text-green-500' : 'text-red-500';
  const Icon = value ? CircleCheck : CircleX;
  const text = value ? 'Sucesso' : 'Falha';
  return { Icon, statusClass, text };
}

function renderByValueType(val: any) {
  if (val === null || val === undefined)
    return <span className="text-gray-400">–</span>;
  if (typeof val === 'object') {
    return (
      <pre className="whitespace-pre-wrap text-xs font-mono bg-gray-50 p-2 rounded">
        {JSON.stringify(val, null, 2)}
      </pre>
    );
  }
  return String(val);
}

// Simple diff function for configuration objects
function diffObjects(desired: any, applied: any, prefix = ''): Array<{ path: string; desired: any; applied: any }> {
  const diffs: Array<{ path: string; desired: any; applied: any }> = [];

  if (!desired || typeof desired !== 'object') return diffs;

  for (const key in desired) {
    const path = prefix ? `${prefix}.${key}` : key;
    const desiredVal = desired[key];
    const appliedVal = applied?.[key];

    if (typeof desiredVal === 'object' && desiredVal !== null && !Array.isArray(desiredVal)) {
      diffs.push(...diffObjects(desiredVal, appliedVal, path));
    } else if (desiredVal !== appliedVal) {
      diffs.push({ path, desired: desiredVal, applied: appliedVal });
    }
  }

  return diffs;
}

export function ConfigurationLogDetailPage() {
  const { id } = useParams<{ id: string }>();

  const { data: log, isLoading, error } = useQuery({
    queryKey: ['configuration-log', id],
    queryFn: () => configurationLogApi.findOne(id!),
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div className="flex-1 space-y-6 p-6">
        <div className="w-full text-center py-8">
          <p className="text-gray-500">Carregando...</p>
        </div>
      </div>
    );
  }

  if (error || !log) {
    return (
      <div>
        <div className="flex flex-wrap items-center gap-6 px-4 sm:flex-nowrap sm:px-6 lg:px-8">
          <Card>
            <CardHeader>
              <CardTitle>Log de configuração não encontrado</CardTitle>
              <CardDescription>
                Não foi possível localizar as informações do log.
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-6 px-4 sm:flex-nowrap sm:px-6 lg:px-8">
        <ConfigurationLogDescription data={log} />
      </div>
    </div>
  );
}

interface ConfigurationLogDescriptionProps {
  data: IConfigurationLog;
}

function ConfigurationLogDescription({ data }: ConfigurationLogDescriptionProps) {
  const formattedCreatedAt = new Date(data.created_at).toLocaleString('pt-BR');
  const { Icon, text } = getStatusProps(data.status);

  const diffEntries = [
    ...diffObjects(
      (data.desired_profile.config as any)?.general ?? {},
      (data.applied_profile as any)?.general ?? {}
    ),
    ...diffObjects(
      (data.desired_profile.config as any)?.specific ?? {},
      (data.applied_profile as any)?.specific ?? {}
    ),
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Log de configuração</CardTitle>
        <CardDescription>
          Detalhes do log de configuração do dispositivo via porta serial.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <dl className="divide-y divide-gray-200">
          {/* Resultado */}
          <div className="px-4 py-6 sm:grid sm:grid-cols-2 sm:gap-4 sm:px-0">
            <dt className="text-sm font-medium text-gray-900">Resultado</dt>
            <dd className="mt-1 sm:mt-0 flex items-center space-x-2">
              <Badge
                className={cn(
                  'flex items-center space-x-1',
                  data.status &&
                    'bg-green-500 hover:bg-green-500/90 text-primary-foreground'
                )}
                variant={data.status ? 'outline' : 'destructive'}
              >
                <Icon className="h-4 w-4" />
                <span>{text}</span>
              </Badge>
            </dd>
          </div>

          {/* Perfil */}
          <div className="px-4 py-6 sm:grid sm:grid-cols-2 sm:gap-4 sm:px-0">
            <dt className="text-sm font-medium text-gray-900">Perfil</dt>
            <dd className="mt-1 text-sm text-gray-700 sm:mt-0">
              {data.desired_profile.name}
            </dd>
          </div>

          {/* Equipamento */}
          <div className="px-4 py-6 sm:grid sm:grid-cols-2 sm:gap-4 sm:px-0">
            <dt className="text-sm font-medium text-gray-900">Serial</dt>
            <dd className="mt-1 text-sm text-gray-700 sm:mt-0">
              {data.equipment.serial}
            </dd>
          </div>

          <div className="px-4 py-6 sm:grid sm:grid-cols-2 sm:gap-4 sm:px-0">
            <dt className="text-sm font-medium text-gray-900">Firmware</dt>
            <dd className="mt-1 text-sm text-gray-700 sm:mt-0">
              {data.equipment.firmware}
            </dd>
          </div>

          {data.equipment.imei && (
            <div className="px-4 py-6 sm:grid sm:grid-cols-2 sm:gap-4 sm:px-0">
              <dt className="text-sm font-medium text-gray-900">IMEI</dt>
              <dd className="mt-1 text-sm text-gray-700 sm:mt-0">
                {data.equipment.imei}
              </dd>
            </div>
          )}

          {data.equipment.iccid && (
            <div className="px-4 py-6 sm:grid sm:grid-cols-2 sm:gap-4 sm:px-0">
              <dt className="text-sm font-medium text-gray-900">ICCID</dt>
              <dd className="mt-1 text-sm text-gray-700 sm:mt-0">
                {data.equipment.iccid}
              </dd>
            </div>
          )}

          {data.equipment.lora_keys && (
            <div className="px-4 py-6 sm:grid sm:grid-cols-2 sm:gap-4 sm:px-0">
              <dt className="text-sm font-medium text-gray-900">LoRa Keys</dt>
              <dd className="mt-1 text-sm text-gray-700 sm:mt-0">
                <dl className="divide-y divide-gray-100">
                  {Object.entries(data.equipment.lora_keys).map(
                    ([key, val]) => (
                      <div key={key} className="py-2 sm:grid sm:grid-cols-2 sm:gap-4">
                        <dt className="text-sm font-medium text-gray-900">
                          {key}
                        </dt>
                        <dd className="mt-1 text-sm text-gray-700 sm:mt-0">
                          {String(val)}
                        </dd>
                      </div>
                    )
                  )}
                </dl>
              </dd>
            </div>
          )}

          {/* Tecnologia */}
          <div className="px-4 py-6 sm:grid sm:grid-cols-2 sm:gap-4 sm:px-0">
            <dt className="text-sm font-medium text-gray-900">Modelo</dt>
            <dd className="mt-1 text-sm text-gray-700 sm:mt-0">
              {DEVICE_MODEL_LABELS[data.technology.system_name as keyof typeof DEVICE_MODEL_LABELS]}
            </dd>
          </div>

          {/* Verificado */}
          <div className="px-4 py-6 sm:grid sm:grid-cols-2 sm:gap-4 sm:px-0">
            <dt className="text-sm font-medium text-gray-900">Verificado</dt>
            <dd className="mt-1 text-sm text-gray-700 sm:mt-0">
              {data.checked ? 'Sim' : 'Não'}
            </dd>
          </div>

          <div className="px-4 py-6 sm:grid sm:grid-cols-2 sm:gap-4 sm:px-0">
            <dt className="text-sm font-medium text-gray-900">Verificado em</dt>
            <dd className="mt-1 text-sm text-gray-700 sm:mt-0">
              {data.checked_at ? new Date(data.checked_at).toLocaleString('pt-BR') : '--'}
            </dd>
          </div>

          {/* Criado em */}
          <div className="px-4 py-6 sm:grid sm:grid-cols-2 sm:gap-4 sm:px-0">
            <dt className="text-sm font-medium text-gray-900">Criado em</dt>
            <dd className="mt-1 text-sm text-gray-700 sm:mt-0">
              {formattedCreatedAt}
            </dd>
          </div>

          {/* Usuário */}
          <div className="px-4 py-6 sm:grid sm:grid-cols-2 sm:gap-4 sm:px-0">
            <dt className="text-sm font-medium text-gray-900">Usuário</dt>
            <dd className="mt-1 text-sm text-gray-700 sm:mt-0">{data.user.name}</dd>
          </div>

          <div className="px-4 py-6 sm:grid sm:grid-cols-2 sm:gap-4 sm:px-0">
            <dt className="text-sm font-medium text-gray-900">Duração</dt>
            <dd className="mt-1 text-sm text-gray-700 sm:mt-0">
              {(data.end_time - data.init_time) / 1000} s
            </dd>
          </div>
        </dl>

        <Separator className="my-4" />

        <div className="mt-6">
          <h3 className="text-lg font-medium text-gray-900">
            Mensagens enviadas
          </h3>
          {data.messages.length > 0 ? (
            <Table className="mt-2">
              <TableHeader>
                <TableRow>
                  <TableHead>Campo</TableHead>
                  <TableHead>Mensagem</TableHead>
                  <TableHead>Resposta</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.messages.map(({ key, request, response }) => (
                  <TableRow key={key}>
                    <TableCell>{key}</TableCell>
                    <TableCell>{request}</TableCell>
                    <TableCell>{renderByValueType(response) ?? '--'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-sm text-gray-600 mt-2">
              Nenhum mensagem foi enviada.
            </p>
          )}
        </div>

        <Separator className="col-span-3 my-4" />

        <div className="mt-6">
          <h3 className="text-lg font-medium text-gray-900">
            Diferenças entre o perfil desejado e aplicado
          </h3>
          {diffEntries.length > 0 ? (
            <Table className="mt-2">
              <TableHeader>
                <TableRow>
                  <TableHead>Campo</TableHead>
                  <TableHead>Desejado</TableHead>
                  <TableHead>Aplicado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {diffEntries.map(({ path, desired, applied }) => (
                  <TableRow key={path}>
                    <TableCell>{path}</TableCell>
                    <TableCell>{renderByValueType(desired)}</TableCell>
                    <TableCell>{renderByValueType(applied)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-sm text-gray-600 mt-2">
              Nenhum diferença encontrada.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
